import { createFeatureSelector, createSelector } from '@ngrx/store';
import { List, Map } from 'immutable';
import { handleActions } from 'redux-actions';
import { Dashboard } from '../reports/models/dashboard.model';
import { ReportType } from '../reports/models/report-type.model';
import { Report } from '../reports/models/report.model';
import {
  ActionsEnum,
  FetchDashboardError,
  FetchExpandedView,
  FetchExpandedViewSuccess,
  FetchHomeMetricsError,
  FetchReportError,
  UpdateCurrentReport,
  UpdateCurrentReportFilterValues,
  UpdateDashboard,
  UpdateHomeMetrics,
  UpdateReportTypes,
} from './report-data.actions';
import { HomeMetrics } from './models/home-metrics.model';
import { ExpandedSummary } from './models/expanded-summary.model';
import { ReportExpandedViewTypesEnum } from './report-data.data';

// feature name
export const FEATURE_NAME = 'report-data';

// state interface
export interface IState {
  dashboardFetched: boolean;
  fetchDashboardError: any[];
  dashboard: Dashboard;
  reportFetched: boolean;
  fetchReportError: any[];
  reportTypes: List<ReportType>;
  currentReport: Report;
  currentReportType: ReportType;
  currentReportFilterValues: any;
  homeMetrics: HomeMetrics;
  homeMetricsError: any[];
  // insights hub
  expandedSummaryItems: Map<string, ExpandedSummary>;
}

// initial state of store
export const initialState: IState = {
  dashboardFetched: true,
  fetchDashboardError: null,
  dashboard: null,
  reportFetched: false,
  fetchReportError: null,
  reportTypes: null,
  currentReport: null,
  currentReportType: null,
  currentReportFilterValues: null,
  homeMetrics: null,
  homeMetricsError: null,
  // insights hub
  expandedSummaryItems: Map<string, ExpandedSummary>({}),
};

// reducer
const reducerFn = handleActions(
  {
    [ActionsEnum.FETCH_DASHBOARD]: function (state: IState): IState {
      return {
        ...state,
        dashboardFetched: false,
      };
    },
    [ActionsEnum.FETCH_DASHBOARD_SUCCESS]: function (state: IState): IState {
      return {
        ...state,
        dashboardFetched: true,
        fetchDashboardError: null,
      };
    },
    [ActionsEnum.FETCH_DASHBOARD_ERROR]: function (
      state: IState,
      { payload }: FetchDashboardError
    ): IState {
      return {
        ...state,
        dashboardFetched: true,
        fetchDashboardError: payload,
      };
    },
    [ActionsEnum.UPDATE_DASHBOARD]: function (
      state: IState,
      { payload }: UpdateDashboard
    ): IState {
      return {
        ...state,
        dashboard: payload,
      };
    },
    [ActionsEnum.RESET_REPORTS_DATA]: function (state: IState): IState {
      return {
        ...state,
        reportTypes: null,
        currentReport: null,
        currentReportType: null,
        currentReportFilterValues: null,
      };
    },
    [ActionsEnum.UPDATE_REPORT_TYPES]: function (
      state: IState,
      { payload }: UpdateReportTypes
    ): IState {
      return {
        ...state,
        reportTypes: payload,
      };
    },
    [ActionsEnum.UPDATE_CURRENT_REPORT]: function (
      state: IState,
      { payload }: UpdateCurrentReport
    ): IState {
      const { report, reportType } = payload;

      return {
        ...state,
        currentReport: report,
        currentReportType: reportType,
        reportFetched: !!report && !!reportType,
      };
    },
    [ActionsEnum.CLEAR_CURRENT_REPORT]: function (state: IState): IState {
      return {
        ...state,
        currentReport: null,
        currentReportType: null,
        currentReportFilterValues: null,
        fetchReportError: null,
      };
    },
    [ActionsEnum.UPDATE_CURRENT_REPORT_FILTER_VALUES]: function (
      state: IState,
      { payload }: UpdateCurrentReportFilterValues
    ): IState {
      return {
        ...state,
        currentReportFilterValues: payload,
      };
    },
    [ActionsEnum.FETCH_REPORT]: function (state: IState): IState {
      return {
        ...state,
        reportFetched: false,
      };
    },
    [ActionsEnum.FETCH_REPORT_SUCCESS]: function (state: IState): IState {
      return {
        ...state,
        reportFetched: true,
        fetchReportError: null,
      };
    },
    [ActionsEnum.FETCH_REPORT_ERROR]: function (
      state: IState,
      { payload }: FetchReportError
    ): IState {
      return {
        ...state,
        reportFetched: true,
        fetchReportError: payload,
      };
    },
    [ActionsEnum.UPDATE_HOME_METRICS]: function (
      state: IState,
      { payload }: UpdateHomeMetrics
    ): IState {
      const { homeMetrics } = payload;

      return {
        ...state,
        homeMetrics,
      };
    },
    [ActionsEnum.FETCH_HOME_METRICS_ERROR]: function (
      state: IState,
      { payload }: FetchHomeMetricsError
    ): IState {
      return {
        ...state,
        homeMetricsError: payload,
      };
    },
    [ActionsEnum.FETCH_EXPANDED_VIEW]: function (
      state: IState,
      { payload }: FetchExpandedView
    ): IState {
      const { types } = payload;

      /**
       * Set the list as null so that we don't
       * show stale information while we're updating the store
       */
      const updatedSummaryItems = {
        ...state.expandedSummaryItems,
      };
      types.forEach((type: ReportExpandedViewTypesEnum) => {
        updatedSummaryItems[type] = false;
      });

      return {
        ...state,
        expandedSummaryItems: Map<string, ExpandedSummary>(updatedSummaryItems),
      };
    },
    [ActionsEnum.FETCH_EXPANDED_VIEW_SUCCESS]: function (
      state: IState,
      { payload }: FetchExpandedViewSuccess
    ): IState {
      const { expandedSummaryItems } = payload;

      /**
       * Replace existing items, Add new items
       */
      const updatedSummaryItems = {
        ...state.expandedSummaryItems,
      };
      expandedSummaryItems.forEach((summary: ExpandedSummary) => {
        updatedSummaryItems[summary.type] = summary;
      });

      return {
        ...state,
        expandedSummaryItems: Map<string, ExpandedSummary>(updatedSummaryItems),
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getDashboardFetched = createSelector(
  selectFeature,
  (state: IState) => state.dashboardFetched
);

export const getFetchDashboardError = createSelector(
  selectFeature,
  (state: IState) => state.fetchDashboardError
);

export const getDashboard = createSelector(
  selectFeature,
  (state: IState) => state.dashboard
);

export const getReportFetched = createSelector(
  selectFeature,
  (state: IState) => state.reportFetched
);

export const getFetchReportError = createSelector(
  selectFeature,
  (state: IState) => state.fetchReportError
);

export const getReportTypes = createSelector(
  selectFeature,
  (state: IState) => state.reportTypes
);

export const getCurrentReport = createSelector(
  selectFeature,
  (state: IState) => state.currentReport
);

export const getCurrentReportType = createSelector(
  selectFeature,
  (state: IState) => state.currentReportType
);

export const getCurrentReportFilterValues = createSelector(
  selectFeature,
  (state: IState) => state.currentReportFilterValues
);

export const getHomeMetrics = createSelector(
  selectFeature,
  (state: IState) => state.homeMetrics
);

export const getHomeMetricsError = createSelector(
  selectFeature,
  (state: IState) => state.homeMetricsError
);

export const getExpandedSummaryItems = createSelector(
  selectFeature,
  (state: IState) => state.expandedSummaryItems
);
